import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ChevronLeft, ChevronRight, Plus, X, Pencil, Trash2, LogIn, RefreshCw, Calendar as CalendarIcon, Repeat, Play } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import SEOHead from '../components/SEOHead';

const API_URL = "";

// Helper function to strip HTML tags from description.
// Uses regex on a string (no DOM mutation) so no auto-resource attributes
// can fire even if the result were attached to the DOM. Output is plain text.
const stripHtmlTags = (html) => {
  if (!html) return '';
  return String(html)
    .replace(/<[^>]*>/g, ' ')   // remove tags
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
};

const CATEGORIES = [
  { value: 'Class', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 'Workshop', color: 'bg-purple-100 text-purple-800 border-purple-200' },
  { value: 'Life Drawing', color: 'bg-cafa-red-100 text-cafa-red-900 border-cafa-blue-200' },
  { value: 'Meeting', color: 'bg-green-100 text-green-800 border-green-200' },
  { value: 'Event', color: 'bg-rose-100 text-rose-800 border-rose-200' },
  { value: 'Exhibition', color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  { value: 'Open Session', color: 'bg-teal-100 text-teal-800 border-teal-200' },
];

// Location-based color mapping
const LOCATION_COLORS = {
  'Studio': { bg: '#22c55e', text: 'white', class: 'bg-green-500 text-white border-green-600' },
  'Museum': { bg: '#3b82f6', text: 'white', class: 'bg-blue-500 text-white border-blue-600' },
  'Apartment': { bg: '#ec4899', text: 'white', class: 'bg-pink-500 text-white border-pink-600' },
  'Gallery': { bg: '#d4a574', text: 'white', class: 'bg-amber-400 text-amber-900 border-amber-500' },
};

const getCategoryColor = (category) => {
  const cat = CATEGORIES.find(c => c.value === category);
  return cat ? cat.color : 'bg-gray-100 text-gray-800 border-gray-200';
};

// Get event color based on location color field or category
const getEventColor = (event) => {
  // If event has a color field (hex color), use it
  if (event.color) {
    // Map hex colors to location
    if (event.color === '#22c55e') return LOCATION_COLORS['Studio'].class;
    if (event.color === '#3b82f6') return LOCATION_COLORS['Museum'].class;
    if (event.color === '#ec4899') return LOCATION_COLORS['Apartment'].class;
    if (event.color === '#d4a574') return LOCATION_COLORS['Gallery'].class;
  }
  
  // If event has location field matching our locations
  if (event.location && LOCATION_COLORS[event.location]) {
    return LOCATION_COLORS[event.location].class;
  }
  
  // Fall back to category color
  return getCategoryColor(event.category);
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

export const Calendar = () => {
  const { user, token, isAuthenticated: authIsAuthenticated } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showEventDetailDialog, setShowEventDetailDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Check if user is admin
  const isAdmin = user?.access && ['Administrator', 'Webmaster', 'Admin'].includes(user.access);
  
  const [newEvent, setNewEvent] = useState({
    title: '',
    date: '',
    start_time: '',
    end_time: '',
    description: '',
    category: 'Class',
    instructor: '',
    price: '',
    location: '115 Melrose Place, San Antonio, TX',
    color: ''
  });

  // Simplified Recurring Event State (replaces templates)
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringSettings, setRecurringSettings] = useState({
    pattern: 'weekly',  // daily, weekly, biweekly, monthly
    endType: 'date',    // date, occurrences, never
    endDate: '',
    occurrences: 12
  });
  const [creatingRecurring, setCreatingRecurring] = useState(false);
  
  // Series edit dialog
  const [showSeriesEditDialog, setShowSeriesEditDialog] = useState(false);
  const [seriesEditType, setSeriesEditType] = useState('single'); // single, future
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteType, setDeleteType] = useState('single'); // single, future, all

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Fetch events for the current month
  useEffect(() => {
    fetchEvents();
  }, [year, month]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/calendar/events/${year}/${month + 1}`);
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Legacy template functions removed - using simplified recurring events now

  const handleCreateEvent = async () => {
    if (!token) {
      toast.error('Please log in as admin to create events');
      return;
    }
    
    try {
      // Check if creating recurring or single event
      if (isRecurring) {
        setCreatingRecurring(true);
        
        // Build recurring event request
        const recurringRequest = {
          title: newEvent.title,
          start_date: newEvent.date,
          start_time: newEvent.start_time,
          end_time: newEvent.end_time,
          description: newEvent.description,
          category: newEvent.category,
          instructor: newEvent.instructor,
          price: newEvent.price,
          location: newEvent.location,
          color: newEvent.color,
          recurrence_pattern: recurringSettings.pattern,
          end_type: recurringSettings.endType,
          end_date: recurringSettings.endType === 'date' ? recurringSettings.endDate : null,
          occurrences: recurringSettings.endType === 'occurrences' ? recurringSettings.occurrences : null
        };
        
        const response = await fetch(`${API_URL}/api/calendar/events/recurring`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(recurringRequest)
        });
        
        if (response.ok) {
          const result = await response.json();
          toast.success(`Created ${result.events_created} events`, {
            description: `From ${result.first_date} to ${result.last_date}`
          });
          setShowEventDialog(false);
          resetNewEvent();
          fetchEvents();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to create recurring events');
        }
        setCreatingRecurring(false);
      } else {
        // Single event
        const response = await fetch(`${API_URL}/api/calendar/events`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(newEvent)
        });
        if (response.ok) {
          toast.success('Event created');
          setShowEventDialog(false);
          resetNewEvent();
          fetchEvents();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to create event');
        }
      }
    } catch (error) {
      toast.error('Error creating event');
      setCreatingRecurring(false);
    }
  };

  const handleUpdateEvent = async () => {
    if (!token) {
      toast.error('Please log in as admin to update events');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/calendar/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newEvent)
      });
      if (response.ok) {
        toast.success('Event updated');
        setShowEventDialog(false);
        setEditMode(false);
        resetNewEvent();
        fetchEvents();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update event');
      }
    } catch (error) {
      toast.error('Error updating event');
    }
  };

  // Update all future events in a series
  const handleUpdateSeries = async (updateType) => {
    if (!token || !selectedEvent?.series_id) return;
    
    try {
      const params = new URLSearchParams({
        update_type: updateType,
        from_date: selectedEvent.date
      });
      
      const response = await fetch(`${API_URL}/api/calendar/events/series/${selectedEvent.series_id}?${params}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newEvent)
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Updated ${result.modified_count} events`);
        setShowEventDialog(false);
        setShowSeriesEditDialog(false);
        setEditMode(false);
        resetNewEvent();
        fetchEvents();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update series');
      }
    } catch (error) {
      toast.error('Error updating series');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!token) {
      toast.error('Please log in as admin to delete events');
      return;
    }
    
    // Check if this is a recurring event
    if (selectedEvent?.series_id) {
      setShowDeleteConfirm(true);
      return;
    }
    
    if (!confirm('Are you sure you want to delete this event?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/calendar/events/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Event deleted');
        setShowEventDetailDialog(false);
        fetchEvents();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete event');
      }
    } catch (error) {
      toast.error('Error deleting event');
    }
  };

  // Delete from series
  const handleDeleteFromSeries = async (deleteType) => {
    if (!token || !selectedEvent) return;
    
    try {
      if (deleteType === 'single') {
        // Delete just this one event
        const response = await fetch(`${API_URL}/api/calendar/events/${selectedEvent.id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          toast.success('Event deleted');
        }
      } else {
        // Delete this and future events
        const params = new URLSearchParams({
          delete_type: 'future',
          from_date: selectedEvent.date
        });
        const response = await fetch(`${API_URL}/api/calendar/events/series/${selectedEvent.series_id}?${params}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const result = await response.json();
          toast.success(`Deleted ${result.deleted_count} events`);
        }
      }
      
      setShowDeleteConfirm(false);
      setShowEventDetailDialog(false);
      fetchEvents();
    } catch (error) {
      toast.error('Error deleting events');
    }
  };

  const resetNewEvent = () => {
    setNewEvent({
      title: '',
      date: '',
      start_time: '',
      end_time: '',
      description: '',
      category: 'Class',
      instructor: '',
      price: '',
      location: '115 Melrose Place, San Antonio, TX',
      color: ''
    });
    setSelectedEvent(null);
    setEditMode(false);
    // Reset recurring settings
    setIsRecurring(false);
    setRecurringSettings({
      pattern: 'weekly',
      endType: 'date',
      endDate: '',
      occurrences: 12
    });
  };

  const openEditDialog = (event) => {
    setSelectedEvent(event);
    setNewEvent({
      title: event.title,
      date: event.date,
      start_time: event.start_time,
      end_time: event.end_time,
      description: event.description || '',
      category: event.category || 'Class',
      instructor: event.instructor || '',
      price: event.price || '',
      location: event.location || '115 Melrose Place, San Antonio, TX',
      color: event.color || ''
    });
    setEditMode(true);
    setShowEventDetailDialog(false);
    setShowEventDialog(true);
  };

  const openEventDetail = (event) => {
    setSelectedEvent(event);
    setShowEventDetailDialog(true);
  };

  // Calendar grid calculations
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const calendarDays = [];
  
  // Previous month days
  for (let i = firstDayOfMonth - 1; i >= 0; i--) {
    calendarDays.push({
      day: daysInPrevMonth - i,
      isCurrentMonth: false,
      date: new Date(year, month - 1, daysInPrevMonth - i)
    });
  }
  
  // Current month days
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push({
      day: i,
      isCurrentMonth: true,
      date: new Date(year, month, i)
    });
  }
  
  // Next month days
  const remainingDays = 42 - calendarDays.length;
  for (let i = 1; i <= remainingDays; i++) {
    calendarDays.push({
      day: i,
      isCurrentMonth: false,
      date: new Date(year, month + 1, i)
    });
  }

  const getEventsForDay = (date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return events.filter(event => event.date === dateStr);
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const goToPrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const goToMonth = (newMonth) => {
    setCurrentDate(new Date(year, parseInt(newMonth), 1));
  };

  const goToYear = (newYear) => {
    setCurrentDate(new Date(parseInt(newYear), month, 1));
  };

  return (
    <div className="min-h-screen bg-white" data-testid="calendar-page">
      <SEOHead
        title="Event Calendar"
        description="Upcoming classes, workshops, and events at The Coppini Academy of Fine Art in San Antonio, TX."
        canonicalPath="/calendar"
      />
      {/* Header */}
      <section className="bg-gradient-to-b from-stone-50 to-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-serif text-stone-900">Event Calendar</h1>
              <p className="text-stone-600 mt-1">Classes, workshops, and events at CAFA</p>
            </div>
            
            <div className="flex items-center gap-2">
              {isAdmin ? (
                <>
                  <Button
                    onClick={() => {
                      resetNewEvent();
                      setShowEventDialog(true);
                    }}
                    className="bg-cafa-red hover:bg-cafa-red-900 text-white"
                    data-testid="add-event-btn"
                  >
                    <Plus size={18} className="mr-1" /> Add Event
                  </Button>
                </>
              ) : !authIsAuthenticated ? (
                <Button
                  variant="outline"
                  asChild
                  className="border-stone-300"
                  data-testid="login-btn"
                >
                  <Link to="/members/login">
                    <LogIn size={18} className="mr-1" /> Admin Login
                  </Link>
                </Button>
              ) : (
                <p className="text-sm text-stone-500">Admin access required to edit</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Calendar Navigation */}
      <section className="border-b border-stone-200 bg-white sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <h2 className="text-2xl font-serif text-stone-900">
              {MONTHS[month]}, {year}
            </h2>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={goToPrevMonth} data-testid="prev-month-btn">
                  <ChevronLeft size={20} /> Previous Month
                </Button>
                <Button variant="ghost" onClick={goToNextMonth} data-testid="next-month-btn">
                  Next Month <ChevronRight size={20} />
                </Button>
              </div>
              
              <div className="flex items-center gap-2">
                <Select value={String(month)} onValueChange={goToMonth}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => (
                      <SelectItem key={i} value={String(i)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={String(year)} onValueChange={goToYear}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027, 2028].map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calendar Grid */}
      <section className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Day Headers */}
          <div className="grid grid-cols-7 border-b border-stone-300 bg-stone-100">
            {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
              <div key={day} className="py-3 text-center text-sm font-semibold text-stone-700 border-r border-stone-300 last:border-r-0">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar Days */}
          <div className="grid grid-cols-7 border-l border-stone-300">
            {calendarDays.map((dayObj, index) => {
              const dayEvents = getEventsForDay(dayObj.date);
              const isToday = dayObj.isCurrentMonth && 
                dayObj.day === new Date().getDate() && 
                month === new Date().getMonth() && 
                year === new Date().getFullYear();
              
              return (
                <div
                  key={index}
                  className={`min-h-[120px] border-r border-b border-stone-300 p-1 ${
                    dayObj.isCurrentMonth ? 'bg-white' : 'bg-stone-50'
                  }`}
                >
                  <div className={`text-right text-sm mb-1 ${
                    dayObj.isCurrentMonth ? 'text-stone-900' : 'text-stone-400'
                  } ${isToday ? 'font-bold' : ''}`}>
                    <span className={isToday ? 'bg-cafa-red text-white rounded-full px-2 py-0.5' : ''}>
                      {dayObj.day}
                    </span>
                  </div>
                  
                  <div className="space-y-0.5 overflow-hidden">
                    {dayEvents.slice(0, 4).map((event, i) => (
                      <div
                        key={event.id}
                        onClick={() => openEventDetail(event)}
                        className={`text-xs p-1 rounded border cursor-pointer hover:opacity-80 truncate ${getEventColor(event)}`}
                        title={`${event.title} - ${formatTime(event.start_time)}`}
                      >
                        <span className="font-medium">{formatTime(event.start_time)}</span>
                        <span className="ml-1">{event.title}</span>
                        {event.instructor && <span className="block text-[10px] opacity-75">{event.instructor}</span>}
                      </div>
                    ))}
                    {dayEvents.length > 4 && (
                      <div className="text-xs text-stone-500 text-center">
                        +{dayEvents.length - 4} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Location Legend */}
      <section className="py-6 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-stone-600 mb-3 font-medium">Location Color Codes</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <span className="px-4 py-2 rounded-full text-sm font-medium bg-green-500 text-white">
              Studio
            </span>
            <span className="px-4 py-2 rounded-full text-sm font-medium bg-blue-500 text-white">
              Museum
            </span>
            <span className="px-4 py-2 rounded-full text-sm font-medium bg-pink-500 text-white">
              Apartment
            </span>
            <span className="px-4 py-2 rounded-full text-sm font-medium bg-amber-400 text-amber-900">
              Gallery
            </span>
          </div>
        </div>
      </section>

      {/* Event Create/Edit Dialog */}
      <Dialog open={showEventDialog} onOpenChange={(open) => {
        setShowEventDialog(open);
        if (!open) resetNewEvent();
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Edit Event' : 'Add New Event'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Event Title *</Label>
                <Input
                  id="title"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent(prev => ({...prev, title: e.target.value}))}
                  placeholder="e.g., Beginning Painting with Roberta Buckles"
                  data-testid="event-title-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={newEvent.date}
                    onChange={(e) => setNewEvent(prev => ({...prev, date: e.target.value}))}
                    data-testid="event-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select value={newEvent.category} onValueChange={(v) => setNewEvent(prev => ({...prev, category: v}))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.value}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Repeat Event Toggle - Only show when creating new event */}
              {!editMode && (
                <div className="p-4 bg-stone-50 rounded-lg border border-stone-200">
                  <div className="flex items-center justify-between mb-3">
                    <Label htmlFor="repeat-toggle" className="font-medium cursor-pointer">
                      Repeat Event
                    </Label>
                    <button
                      id="repeat-toggle"
                      type="button"
                      onClick={() => setIsRecurring(!isRecurring)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        isRecurring ? 'bg-cafa-red' : 'bg-stone-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        isRecurring ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                  
                  {isRecurring && (
                    <div className="space-y-4 pt-2 border-t border-stone-200">
                      {/* Recurrence Pattern */}
                      <div className="space-y-2">
                        <Label>Repeats</Label>
                        <Select 
                          value={recurringSettings.pattern} 
                          onValueChange={(v) => setRecurringSettings({...recurringSettings, pattern: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                        {newEvent.date && (
                          <p className="text-sm text-stone-500">
                            {recurringSettings.pattern === 'daily' ? (
                              'Every day'
                            ) : (
                              <>
                                Every {recurringSettings.pattern === 'biweekly' ? 'other ' : ''}
                                {new Date(newEvent.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' })}
                                {recurringSettings.pattern === 'monthly' && ' of the month'}
                              </>
                            )}
                          </p>
                        )}
                      </div>
                      
                      {/* End Type */}
                      <div className="space-y-2">
                        <Label>Ends</Label>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="endType"
                              value="date"
                              checked={recurringSettings.endType === 'date'}
                              onChange={() => setRecurringSettings({...recurringSettings, endType: 'date'})}
                              className="text-cafa-red"
                            />
                            <span className="text-sm">On date</span>
                            {recurringSettings.endType === 'date' && (
                              <Input
                                type="date"
                                value={recurringSettings.endDate}
                                onChange={(e) => setRecurringSettings({...recurringSettings, endDate: e.target.value})}
                                className="ml-2 w-40 h-8"
                                min={newEvent.date}
                              />
                            )}
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="endType"
                              value="occurrences"
                              checked={recurringSettings.endType === 'occurrences'}
                              onChange={() => setRecurringSettings({...recurringSettings, endType: 'occurrences'})}
                              className="text-cafa-red"
                            />
                            <span className="text-sm">After</span>
                            {recurringSettings.endType === 'occurrences' && (
                              <Input
                                type="number"
                                value={recurringSettings.occurrences}
                                onChange={(e) => setRecurringSettings({...recurringSettings, occurrences: parseInt(e.target.value) || 1})}
                                className="ml-2 w-20 h-8"
                                min="1"
                                max="52"
                              />
                            )}
                            <span className="text-sm">sessions</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="endType"
                              value="never"
                              checked={recurringSettings.endType === 'never'}
                              onChange={() => setRecurringSettings({...recurringSettings, endType: 'never'})}
                              className="text-cafa-red"
                            />
                            <span className="text-sm">Never (up to 1 year)</span>
                          </label>
                        </div>
                      </div>
                      
                      {/* Smart Preview */}
                      {newEvent.date && (
                        <div className="p-3 bg-amber-50 rounded-md border border-amber-200">
                          <p className="text-sm text-amber-800">
                            {(() => {
                              // Calculate preview
                              const startDate = new Date(newEvent.date + 'T00:00:00');
                              let count = 0;
                              let endDate = new Date(startDate);
                              
                              if (recurringSettings.endType === 'occurrences') {
                                count = Math.min(recurringSettings.occurrences || 12, 365); // Allow up to 365 for daily
                                if (recurringSettings.pattern === 'daily') {
                                  endDate = new Date(startDate);
                                  endDate.setDate(endDate.getDate() + count - 1);
                                } else if (recurringSettings.pattern === 'monthly') {
                                  endDate = new Date(startDate);
                                  endDate.setMonth(endDate.getMonth() + count - 1);
                                } else {
                                  const weeksToAdd = recurringSettings.pattern === 'biweekly' 
                                    ? (count - 1) * 2 
                                    : count - 1;
                                  endDate = new Date(startDate);
                                  endDate.setDate(endDate.getDate() + weeksToAdd * 7);
                                }
                              } else if (recurringSettings.endType === 'date' && recurringSettings.endDate) {
                                endDate = new Date(recurringSettings.endDate + 'T00:00:00');
                                const diffDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
                                if (recurringSettings.pattern === 'daily') {
                                  count = diffDays + 1;
                                } else if (recurringSettings.pattern === 'weekly') {
                                  count = Math.floor(diffDays / 7) + 1;
                                } else if (recurringSettings.pattern === 'biweekly') {
                                  count = Math.floor(diffDays / 14) + 1;
                                } else {
                                  count = Math.floor(diffDays / 30) + 1;
                                }
                                count = Math.min(count, 365); // Allow up to 365 for daily
                              } else {
                                // "never" - 1 year max
                                endDate = new Date(startDate);
                                endDate.setFullYear(endDate.getFullYear() + 1);
                                count = recurringSettings.pattern === 'daily' ? 365 
                                  : recurringSettings.pattern === 'weekly' ? 52 
                                  : recurringSettings.pattern === 'biweekly' ? 26 
                                  : 12;
                              }
                              
                              const formatDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                              return `This will create ~${count} sessions from ${formatDate(startDate)} through ${formatDate(endDate)}`;
                            })()}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_time">Start Time *</Label>
                  <input
                    id="start_time"
                    type="time"
                    value={newEvent.start_time}
                    onChange={(e) => setNewEvent(prev => ({...prev, start_time: e.target.value}))}
                    className="flex h-9 w-full rounded-md border border-stone-300 bg-white px-3 py-1 text-base shadow-sm focus:outline-none focus:ring-1 focus:ring-cafa-red"
                    data-testid="event-start-time-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_time">End Time *</Label>
                  <input
                    id="end_time"
                    type="time"
                    value={newEvent.end_time}
                    onChange={(e) => setNewEvent(prev => ({...prev, end_time: e.target.value}))}
                    className="flex h-9 w-full rounded-md border border-stone-300 bg-white px-3 py-1 text-base shadow-sm focus:outline-none focus:ring-1 focus:ring-cafa-red"
                    data-testid="event-end-time-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="instructor">Instructor</Label>
                <Input
                  id="instructor"
                  value={newEvent.instructor}
                  onChange={(e) => setNewEvent(prev => ({...prev, instructor: e.target.value}))}
                  placeholder="e.g., Roberta Buckles"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="price">Price</Label>
                <Input
                  id="price"
                  value={newEvent.price}
                  onChange={(e) => setNewEvent(prev => ({...prev, price: e.target.value}))}
                  placeholder="e.g., $225 per student"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  className="w-full min-h-[80px] px-3 py-2 border border-stone-300 rounded-md text-sm"
                  value={newEvent.description}
                  onChange={(e) => setNewEvent(prev => ({...prev, description: e.target.value}))}
                  placeholder="Event description..."
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Select 
                  value={newEvent.location} 
                  onValueChange={(value) => {
                    // Auto-set color based on location
                    let color = '';
                    if (value === 'Studio') color = '#22c55e';
                    else if (value === 'Museum') color = '#3b82f6';
                    else if (value === 'Apartment') color = '#ec4899';
                    else if (value === 'Gallery') color = '#d4a574';
                    setNewEvent(prev => ({...prev, location: value, color: color}));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Studio">Studio (Green)</SelectItem>
                    <SelectItem value="Museum">Museum (Blue)</SelectItem>
                    <SelectItem value="Apartment">Apartment (Pink)</SelectItem>
                    <SelectItem value="Gallery">Gallery (Tan)</SelectItem>
                    <SelectItem value="115 Melrose Place, San Antonio, TX">Main Building</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex gap-2 pt-4">
              {/* Form validation helper */}
              {(() => {
                const missingFields = [];
                if (!newEvent.title) missingFields.push('title');
                if (!newEvent.date) missingFields.push('date');
                if (!newEvent.start_time) missingFields.push('start time');
                if (!newEvent.end_time) missingFields.push('end time');
                if (isRecurring && recurringSettings.endType === 'date' && !recurringSettings.endDate) {
                  missingFields.push('end date for recurring');
                }
                
                const isFormValid = missingFields.length === 0;
                const isButtonDisabled = !isFormValid || creatingRecurring;
                
                return (
                  <>
                    {missingFields.length > 0 && (
                      <p className="text-sm text-amber-600 mb-2 w-full">
                        Missing: {missingFields.join(', ')}
                      </p>
                    )}
                    <Button 
                      onClick={editMode ? handleUpdateEvent : handleCreateEvent}
                      className="flex-1 bg-cafa-red hover:bg-cafa-red-900"
                      disabled={isButtonDisabled}
                      data-testid="save-event-btn"
                    >
                      {creatingRecurring ? 'Creating Events...' : editMode ? 'Update Event' : isRecurring ? 'Create Recurring Events' : 'Create Event'}
                    </Button>
                  </>
                );
              })()}
              <Button variant="outline" onClick={() => {
                setShowEventDialog(false);
                resetNewEvent();
              }}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Event Detail Dialog */}
      <Dialog open={showEventDetailDialog} onOpenChange={setShowEventDetailDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="pr-8">{selectedEvent?.title}</DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-4 py-4">
              {/* Recurring Series Badge */}
              {selectedEvent.series_id && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-md border border-blue-200">
                  <Repeat size={14} className="text-blue-600" />
                  <span className="text-sm text-blue-700">Part of a Recurring Series</span>
                </div>
              )}
              
              {/* Admin buttons at TOP for easy access */}
              {isAdmin && (
                <div className="flex gap-2 pb-4 border-b border-stone-200">
                  <Button onClick={() => openEditDialog(selectedEvent)} className="flex-1 bg-cafa-red hover:bg-cafa-red-900">
                    <Pencil size={16} className="mr-1" /> Edit
                  </Button>
                  <Button variant="destructive" onClick={() => handleDeleteEvent(selectedEvent.id)}>
                    <Trash2 size={16} className="mr-1" /> Delete
                  </Button>
                </div>
              )}
              
              <div className="flex flex-wrap gap-2">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(selectedEvent.category)}`}>
                  {selectedEvent.category}
                </span>
                {selectedEvent.location && LOCATION_COLORS[selectedEvent.location] && (
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${LOCATION_COLORS[selectedEvent.location].class}`}>
                    {selectedEvent.location}
                  </span>
                )}
              </div>
              
              <div className="space-y-2 text-sm">
                <p><strong>Date:</strong> {new Date(selectedEvent.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                <p><strong>Time:</strong> {formatTime(selectedEvent.start_time)} - {formatTime(selectedEvent.end_time)}</p>
                {selectedEvent.instructor && <p><strong>Instructor:</strong> {selectedEvent.instructor}</p>}
                {selectedEvent.price && <p><strong>Price:</strong> {selectedEvent.price}</p>}
                {selectedEvent.location && <p><strong>Location:</strong> {selectedEvent.location}</p>}
                {selectedEvent.description && (
                  <div>
                    <strong>Description:</strong>
                    <p className="mt-1 text-stone-600">{stripHtmlTags(selectedEvent.description)}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog for Recurring Events */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Recurring Event</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-stone-600 mb-4">
              This event is part of a recurring series. What would you like to delete?
            </p>
            <div className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => handleDeleteFromSeries('single')}
              >
                <X size={16} className="mr-2" />
                Delete only this event
              </Button>
              <Button 
                variant="destructive" 
                className="w-full justify-start"
                onClick={() => handleDeleteFromSeries('future')}
              >
                <Trash2 size={16} className="mr-2" />
                Delete this and all future events
              </Button>
            </div>
            <Button 
              variant="ghost" 
              className="w-full mt-4"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
